import { AnimatePresence, motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EASE_OUT, EMPIRE_RED, SABER_GREEN, STAR_GRAY } from '../constants';
import { HoloCard } from '../ui-helpers';

function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randFloat(min: number, max: number, decimals: number) {
  return parseFloat((Math.random() * (max - min) + min).toFixed(decimals));
}

function AnimatedValue({ value, color }: { value: string; color: string }) {
  return (
    <AnimatePresence mode='popLayout'>
      <motion.span key={value} className='inline-block font-mono text-sm font-bold' style={{ color }} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}>
        {value}
      </motion.span>
    </AnimatePresence>
  );
}

export function DarkVsLight() {
  const { t } = useTranslation(['components/trial/lp/v4b']);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  const [darkPing, setDarkPing] = useState('187ms');
  const [darkLoss, setDarkLoss] = useState('12.4%');
  const [darkJitter, setDarkJitter] = useState('45ms');
  const [lightPing, setLightPing] = useState('3ms');
  const [lightJitter, setLightJitter] = useState('1.2ms');

  const tickDark = useCallback(() => {
    setDarkPing(`${randInt(120, 280)}ms`);
    setDarkLoss(`${randFloat(2, 30, 1)}%`);
    setDarkJitter(`${randInt(20, 80)}ms`);
  }, []);

  useEffect(() => {
    if (!inView) return undefined;
    const id = setInterval(tickDark, 900);
    return () => clearInterval(id);
  }, [inView, tickDark]);

  const tickLight = useCallback(() => {
    setLightPing(`${randInt(1, 5)}ms`);
    setLightJitter(`${randFloat(0.5, 2, 1)}ms`);
  }, []);

  useEffect(() => {
    if (!inView) return undefined;
    const id = setInterval(tickLight, 1200);
    return () => clearInterval(id);
  }, [inView, tickLight]);

  const darkMetrics = [
    { label: t('dark_vs_light.ping'), value: darkPing },
    { label: t('dark_vs_light.packet_loss'), value: darkLoss },
    { label: t('dark_vs_light.jitter'), value: darkJitter },
    { label: t('dark_vs_light.route'), value: t('dark_vs_light.dark_route') },
  ];

  const lightMetrics = [
    { label: t('dark_vs_light.ping'), value: lightPing },
    { label: t('dark_vs_light.packet_loss'), value: '0.0%' },
    { label: t('dark_vs_light.jitter'), value: lightJitter },
    { label: t('dark_vs_light.routes'), value: t('dark_vs_light.light_routes') },
  ];

  return (
    <div ref={ref} className='grid gap-6 md:grid-cols-2'>
      <HoloCard className='p-6 md:p-8' glow={EMPIRE_RED} delay={0}>
        <div className='mb-6 text-center'>
          <span className='text-xs font-semibold uppercase tracking-[0.3em]' style={{ color: EMPIRE_RED, fontFamily: 'var(--font-outfit)' }}>
            {t('dark_vs_light.dark_subtitle')}
          </span>
          <h3 className='mt-2 text-2xl font-bold md:text-3xl' style={{ color: EMPIRE_RED, fontFamily: 'var(--font-bai)' }}>
            {t('dark_vs_light.dark_title')}
          </h3>
        </div>
        <div className='space-y-4'>
          {darkMetrics.map((m, i) => (
            <motion.div key={m.label} className='flex items-center justify-between border-b py-2' style={{ borderColor: `${EMPIRE_RED}15` }} initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }} transition={{ delay: 0.2 + i * 0.1, ease: EASE_OUT }}>
              <span className='text-sm' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                {m.label}
              </span>
              <AnimatedValue value={m.value} color={EMPIRE_RED} />
            </motion.div>
          ))}
        </div>
        <motion.div className='mt-6 text-center font-mono text-xs uppercase tracking-wider' style={{ color: EMPIRE_RED }} animate={inView ? { opacity: [0.4, 1, 0.4] } : undefined} transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}>
          {t('dark_vs_light.dark_status')}
        </motion.div>
      </HoloCard>

      <HoloCard className='p-6 md:p-8' glow={SABER_GREEN} delay={0.2}>
        <div className='mb-6 text-center'>
          <span className='text-xs font-semibold uppercase tracking-[0.3em]' style={{ color: SABER_GREEN, fontFamily: 'var(--font-outfit)' }}>
            {t('dark_vs_light.light_subtitle')}
          </span>
          <h3 className='mt-2 text-2xl font-bold md:text-3xl' style={{ color: SABER_GREEN, fontFamily: 'var(--font-bai)' }}>
            {t('dark_vs_light.light_title')}
          </h3>
        </div>
        <div className='space-y-4'>
          {lightMetrics.map((m, i) => (
            <motion.div key={m.label} className='flex items-center justify-between border-b py-2' style={{ borderColor: `${SABER_GREEN}15` }} initial={{ opacity: 0, x: 20 }} animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }} transition={{ delay: 0.4 + i * 0.1, ease: EASE_OUT }}>
              <span className='text-sm' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                {m.label}
              </span>
              <AnimatedValue value={m.value} color={SABER_GREEN} />
            </motion.div>
          ))}
        </div>
        <motion.div className='mt-6 text-center font-mono text-xs uppercase tracking-wider' style={{ color: SABER_GREEN }} animate={inView ? { textShadow: ['0 0 6px #93FF1866', '0 0 14px #93FF1899', '0 0 6px #93FF1866'] } : undefined} transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}>
          {t('dark_vs_light.light_status')}
        </motion.div>
      </HoloCard>
    </div>
  );
}
